import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  DashboardCard,
  EmptyState,
  Skeleton,
  StatusBadge,
  cn,
} from '@zafroi/ui-kit';
import { Activity, Bot, MessageSquare } from 'lucide-react';
import { useConversations } from '@/hooks/useEngageQueries';
import { relativeTime } from '@/lib/format';
import {
  channelKindLabel,
  conversationStatusVariants,
} from '@/types/status-variants';
import type { Conversation } from '@/types/engage';

/**
 * Bot activity — the audit/improvement-loop surface for bot-handled chats.
 *
 * Sits on the Chatbot Insights tab below the Knowledge gaps section because
 * every bot conversation is raw material for Loops A & B: escalations feed
 * the gap analyser, resolved cases feed the AI-draft pipeline. Showing them
 * here (rather than the now-demoted top-level Conversations module) keeps
 * the bot's improvement loop on a single screen.
 *
 * Filter: `channel === 'portal'` (the customer-facing widget the bot sits
 * on). Sorted by most recent activity; capped at 15 rows — anything older
 * is one click away via the per-row deep-link to `/conversations/:id`.
 */
const MAX_ROWS = 15;

export function BotActivitySection() {
  const navigate = useNavigate();
  const query = useConversations();

  const botConversations = useMemo<Conversation[]>(() => {
    const list = query.data ?? [];
    return list
      .filter((c) => c.channel === 'portal')
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
      .slice(0, MAX_ROWS);
  }, [query.data]);

  return (
    <DashboardCard
      icon={Activity}
      title="Recent bot activity"
      description="The last 15 conversations the bot handled. Each one is signal for what's working and what's missing — escalations feed your gaps, resolved cases feed your drafts."
      flushBody
    >
      {query.isLoading ? (
        <div className="space-y-2 px-5 py-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : botConversations.length === 0 ? (
        <div className="px-5 py-6">
          <EmptyState
            icon={Bot}
            title="No bot conversations yet"
            description="Once customers start chatting via the portal widget, you'll see them here."
          />
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {botConversations.map((conv) => (
            <BotActivityRow
              key={conv.id}
              conv={conv}
              onOpen={() => navigate(`/conversations/${conv.id}`)}
            />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

function BotActivityRow({
  conv,
  onOpen,
}: {
  conv: Conversation;
  onOpen: () => void;
}) {
  const channelLabel = channelKindLabel[conv.channel] ?? conv.channel;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'flex w-full items-start gap-3 px-5 py-3 text-left transition-colors',
          'hover:bg-muted/40 focus:bg-muted/40 focus:outline-none',
        )}
      >
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Bot className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">
              {conv.customerName}
            </span>
            <Badge tone="neutral" withDot={false} className="text-[10px]">
              {channelLabel}
            </Badge>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {truncate(conv.preview, 80)}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 pl-2">
          <div className="flex items-center gap-1.5">
            {conv.unread && (
              <span
                aria-label="Unread"
                title="Unread"
                className="h-1.5 w-1.5 rounded-full bg-primary"
              />
            )}
            <span className="text-[11px] tnum text-muted-foreground">
              {relativeTime(conv.lastMessageAt)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <MessageSquare className="h-3 w-3" />
              {conv.messageCount}
            </span>
            <StatusBadge
              status={conv.status}
              variants={conversationStatusVariants}
            />
          </div>
        </div>
      </button>
    </li>
  );
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}
